import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import useDriverService from '../../services/useDriverService';
import { Driver } from '../../models/DriverModel';
import showToast from '../../utils/toast';
import DriverForm from '../../components/Drivers/DriverForm';
import ButtonCancelForm from '../../components/forms/ButtonCancelForm';
import ButtonSaveForm from '../../components/forms/ButtonSaveForm';
import DriverPassFieldsPanel from './components/DriverPassFieldsPanel';
import DriverPassHistoryPanel from './components/DriverPassHistoryPanel';
import DriverStatusSelector from './components/DriverStatusSelector';
import { useFormErrors } from '../../hooks/useFormErrors';
import {
  buildExtensionsPayload,
  buildInitialPassState,
  DRIVER_PASS_TABS,
  DriverPassTabId,
  getPassTabById,
  mergePassExtensionsFromApi,
} from '../Companies/Passes/passFieldConfig';
import {
  expirationStatusToTone,
  getExpirationStatus,
  getWorstExpirationStatus,
} from '../../utils/passExpirationUtils';
import './styles/EditDriverPage.css';
import { canAccessDriverEditPage, canEditOrDeleteDrivers } from '../../hooks/usePermissions';

type SummaryTone = 'ok' | 'warn' | 'error' | 'muted';
type EditDriverTab = DriverPassTabId | 'history';
type CatalogOption = { value: string; label: string };

const EditDriverPage: React.FC = () => {
  const { id = '0' } = useParams();
  const navigate = useNavigate();
  const { edit, update, updatePassExtensions, fetching } = useDriverService();
  const { fieldErrors, clearErrors, setErrorsFromResponse } = useFormErrors();

  const [driver, setDriver] = useState<Driver | null>(null);
  const [formDraft, setFormDraft] = useState<Driver | null>(null);
  const [showEditModal, setShowEditModal] = useState(false);
  const [passes, setPasses] = useState<CatalogOption[]>([]);
  const [shifts, setShifts] = useState<CatalogOption[]>([]);
  const [shiftGroups, setShiftGroups] = useState<CatalogOption[]>([]);
  const [vehicles, setVehicles] = useState<CatalogOption[]>([]);
  const [activeTab, setActiveTab] = useState<EditDriverTab>('el_abra');
  const [passStates, setPassStates] = useState(buildInitialPassState);
  const [savingPasses, setSavingPasses] = useState(false);
  const [savingDriver, setSavingDriver] = useState(false);
  const [savingStatus, setSavingStatus] = useState(false);
  const [historyRefreshKey, setHistoryRefreshKey] = useState(0);
  const canManageDriver = canEditOrDeleteDrivers();

  const loadDriver = useCallback(() => {
    if (!canAccessDriverEditPage()) return;
    const driverId = Number(id);
    if (!driverId) {
      showToast('Conductor no válido', 'error');
      navigate('/drivers');
      return;
    }

    edit(driverId, {
      onSuccess: (resp: any) => {
        const data = resp?.data ?? {};
        const driverData = data.driver ?? {};

        setPasses(
          (data.passes ?? []).map((p: { id: number; name: string }) => ({
            value: String(p.id),
            label: p.name,
          }))
        );
        setShifts(
          (data.shifts ?? []).map((s: { id: number; name: string }) => ({
            value: String(s.id),
            label: s.name,
          }))
        );
        setShiftGroups(
          (data.shift_groups ?? []).map((g: { id: number; name: string }) => ({
            value: String(g.id),
            label: g.name,
          }))
        );
        setVehicles(
          (data.vehicles ?? []).map((v: { id: number; name: string }) => ({
            value: String(v.id),
            label: v.name,
          }))
        );

        const vehicleId = driverData.vehicle_id ?? driverData.vehicle;

        setDriver({
          ...driverData,
          vehicle: vehicleId ? String(vehicleId) : undefined,
          shift: driverData.shift ?? null,
          passes: Array.isArray(driverData.passes) ? driverData.passes : [],
          shift_groups: Array.isArray(driverData.shift_groups) ? driverData.shift_groups : [],
          status: driverData.status ?? 'Operativo',
        });
        setPassStates(mergePassExtensionsFromApi(data.pass_extensions));
        clearErrors();
      },
      onError: () => {
        showToast('Error cargando datos del conductor', 'error');
        navigate('/drivers');
      },
    });
  }, [clearErrors, edit, id, navigate]);

  useEffect(() => {
    if (!canAccessDriverEditPage()) {
      showToast('No tienes permiso para acceder a esta página', 'error');
      navigate('/drivers', { replace: true });
      return;
    }
    loadDriver();
  }, [id, loadDriver, navigate]);

  const activeTabDef = activeTab !== 'history' ? getPassTabById(activeTab) : null;
  const activePassState = activeTab !== 'history' ? passStates[activeTab] : null;
  const isHistoryTab = activeTab === 'history';

  const driverFullName = useMemo(() => {
    if (!driver) return '';
    return [driver.first_name, driver.last_name].filter(Boolean).join(' ');
  }, [driver]);

  const vehicleLabel = useMemo(() => {
    if (!driver?.vehicle) return '—';
    const matched = vehicles.find((v) => v.value === String(driver.vehicle));
    return matched?.label ?? driver.vehicle_license_plate ?? '—';
  }, [driver, vehicles]);

  const shiftLabel = useMemo(() => {
    if (!driver?.shift) return '—';
    const matched = shifts.find((s) => s.value === String(driver.shift));
    return matched?.label ?? driver.shift_name ?? '—';
  }, [driver, shifts]);

  const summaryItems = useMemo((): { key: string; text: string; tone: SummaryTone }[] => {
    return DRIVER_PASS_TABS.map((tab) => {
      const state = passStates[tab.id as DriverPassTabId];
      const dates = Object.values(state.values).filter(Boolean);
      const worstStatus = getWorstExpirationStatus(dates);

      if (!state.enabled) {
        return { key: tab.id, text: `${tab.label}: inactivo`, tone: 'muted' };
      }
      if (dates.length === 0) {
        return { key: tab.id, text: `${tab.label}: sin fechas registradas`, tone: 'muted' };
      }

      const expiredCount = dates.filter((d) => getExpirationStatus(d) === 'expired').length;
      const warningCount = dates.filter((d) => getExpirationStatus(d) === 'warning').length;

      let detail = 'habilitado';
      if (expiredCount > 0) detail = `${expiredCount} documento(s) expirado(s)`;
      else if (warningCount > 0) detail = `${warningCount} documento(s) por expirar`;

      return {
        key: tab.id,
        text: `${tab.label}: ${detail}`,
        tone: expirationStatusToTone(worstStatus),
      };
    });
  }, [passStates]);

  const buildUpdatePayload = (source: Driver) => ({
    first_name: source.first_name,
    last_name: source.last_name,
    address: source.address,
    passes: source.passes,
    vehicle: source.vehicle ? String(source.vehicle) : undefined,
    phone: source.phone,
    shift: source.shift ? Number(source.shift) : null,
    shift_groups: source.shift_groups,
    status: source.status,
    is_active: source.is_active,
  });

  const handleStatusChange = (status: string) => {
    if (!driver?.id || status === driver.status) return;

    setSavingStatus(true);
    update(driver.id, { ...buildUpdatePayload({ ...driver, status }) }, {
      onSuccess: (resp: any) => {
        setSavingStatus(false);
        setDriver({ ...driver, status });
        showToast(resp?.message ?? 'Estado actualizado', 'success');
      },
      onError: (resp: any) => {
        setSavingStatus(false);
        showToast(resp?.message ?? 'Error al actualizar estado', 'error');
      },
    });
  };

  const openEditModal = () => {
    if (!driver) return;
    setFormDraft({ ...driver });
    clearErrors();
    setShowEditModal(true);
  };

  const closeEditModal = () => {
    clearErrors();
    setShowEditModal(false);
    setFormDraft(null);
  };

  const callbackSaveDriver = async (): Promise<any> => {
    if (!formDraft?.id) return;
    setSavingDriver(true);
    try {
      return await update(formDraft.id, buildUpdatePayload(formDraft));
    } finally {
      setSavingDriver(false);
    }
  };

  const handleSaveDriverComplete = (ok: boolean, resp?: any) => {
    if (ok) {
      if (formDraft) setDriver(formDraft);
      showToast(resp?.message ?? 'Conductor actualizado correctamente', 'success');
      closeEditModal();
      return;
    }
    setErrorsFromResponse(resp);
    showToast(resp?.message ?? 'Error al guardar conductor', 'error');
  };

  const updatePassEnabled = (tabId: DriverPassTabId, enabled: boolean) => {
    setPassStates((prev) => ({
      ...prev,
      [tabId]: { ...prev[tabId], enabled },
    }));
  };

  const updatePassField = (tabId: DriverPassTabId, key: string, value: string) => {
    setPassStates((prev) => ({
      ...prev,
      [tabId]: {
        ...prev[tabId],
        values: { ...prev[tabId].values, [key]: value },
      },
    }));
  };

  const handleSavePasses = () => {
    const driverId = Number(id);
    if (!driverId) return;

    const payload = DRIVER_PASS_TABS.reduce(
      (acc, tab) => {
        const tabId = tab.id as DriverPassTabId;
        const state = passStates[tabId];
        acc[tabId] = {
          enabled: state.enabled,
          extensions: buildExtensionsPayload(tab, state.values),
        };
        return acc;
      },
      {} as Record<DriverPassTabId, { enabled: boolean; extensions: ReturnType<typeof buildExtensionsPayload> }>
    );

    setSavingPasses(true);
    updatePassExtensions(driverId, payload, {
      onSuccess: (resp: any) => {
        setSavingPasses(false);
        setPassStates(mergePassExtensionsFromApi(resp?.data));
        setHistoryRefreshKey((key) => key + 1);
        showToast('Pases guardados correctamente', 'success');
      },
      onError: (resp: any) => {
        setSavingPasses(false);
        showToast(resp?.message ?? 'Error al guardar pases', 'error');
      },
    });
  };

  if (fetching.edit && !driver) {
    return (
      <div className="ed-driver">
        <div className="ed-driver__loading">Cargando conductor…</div>
      </div>
    );
  }

  if (!driver) return null;

  return (
    <div className="ed-driver">
      <header className="ed-driver__header">
        <button type="button" className="ed-driver__back" onClick={() => navigate('/drivers')}>
          ← Volver
        </button>
        <div className="ed-driver__header-center">
          <h1 className="ed-driver__title">Editar conductor</h1>
          {driverFullName && <p className="ed-driver__subtitle">{driverFullName}</p>}
        </div>
        <div className="ed-driver__header-actions">
          {canManageDriver && !isHistoryTab && (
            <button
              type="button"
              className="ed-driver__save ed-driver__save--secondary"
              onClick={handleSavePasses}
              disabled={savingPasses || fetching.updatePassExtensions}
            >
              {savingPasses || fetching.updatePassExtensions ? 'Guardando…' : 'Guardar pases'}
            </button>
          )}
        </div>
      </header>

      <div className="ed-driver__top">
        <section className="ed-driver__card" aria-labelledby="ed-driver-info-title">
          <div className="ed-driver__card-head ed-driver__card-head--split">
            <h2 id="ed-driver-info-title" className="ed-driver__card-title">
              Conductor
            </h2>
            {canManageDriver && (
              <button type="button" className="ed-driver__edit-btn" onClick={openEditModal}>
                Editar datos
              </button>
            )}
          </div>
          <div className="ed-driver__card-body">
            <DriverStatusSelector
              value={driver.status ?? 'Operativo'}
              onChange={handleStatusChange}
              disabled={!canManageDriver || savingStatus || fetching.update}
            />
            <ul className="ed-driver__info-grid">
              <li className="ed-driver__info-item">
                <span className="ed-driver__info-label">Nombre</span>
                <span className="ed-driver__info-value">{driver.first_name || '—'}</span>
              </li>
              <li className="ed-driver__info-item">
                <span className="ed-driver__info-label">Apellido</span>
                <span className="ed-driver__info-value">{driver.last_name || '—'}</span>
              </li>
              <li className="ed-driver__info-item">
                <span className="ed-driver__info-label">Celular</span>
                <span className="ed-driver__info-value">{driver.phone || '—'}</span>
              </li>
              <li className="ed-driver__info-item">
                <span className="ed-driver__info-label">Turno</span>
                <span className="ed-driver__info-value">{shiftLabel}</span>
              </li>
              <li className="ed-driver__info-item ed-driver__info-item--wide">
                <span className="ed-driver__info-label">Vehículo asignado</span>
                <span className="ed-driver__info-value">{vehicleLabel}</span>
              </li>
              <li className="ed-driver__info-item ed-driver__info-item--wide">
                <span className="ed-driver__info-label">Dirección</span>
                <span className="ed-driver__info-value">{driver.address || '—'}</span>
              </li>
            </ul>
          </div>
        </section>

        <section className="ed-driver__card" aria-labelledby="ed-driver-summary-title">
          <div className="ed-driver__card-head">
            <h2 id="ed-driver-summary-title" className="ed-driver__card-title">
              Resumen de vencimientos
            </h2>
          </div>
          <div className="ed-driver__card-body">
            <ul className="ed-driver__summary-list">
              {summaryItems.map((item) => (
                <li
                  key={item.key}
                  className={`ed-driver__summary-item ed-driver__summary-item--${item.tone}`}
                >
                  {item.text}
                </li>
              ))}
            </ul>
          </div>
        </section>
      </div>

      <section className="ed-driver__tabs-panel" aria-label="Pases del conductor">
        <nav className="ed-driver__tabs-nav" role="tablist">
          {DRIVER_PASS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              role="tab"
              aria-selected={activeTab === tab.id}
              className={`ed-driver__tab-btn${activeTab === tab.id ? ' is-active' : ''}`}
              onClick={() => setActiveTab(tab.id as DriverPassTabId)}
            >
              {tab.label}
            </button>
          ))}
          <button
            type="button"
            role="tab"
            aria-selected={isHistoryTab}
            className={`ed-driver__tab-btn${isHistoryTab ? ' is-active' : ''}`}
            onClick={() => setActiveTab('history')}
          >
            Historial
          </button>
        </nav>

        <div className="ed-driver__tab-content" role="tabpanel">
          {isHistoryTab ? (
            <DriverPassHistoryPanel driverId={Number(id)} refreshKey={historyRefreshKey} />
          ) : activeTabDef && activePassState ? (
            <DriverPassFieldsPanel
              tab={activeTabDef}
              state={activePassState}
              onToggle={(enabled) => updatePassEnabled(activeTab, enabled)}
              onFieldChange={(key, value) => updatePassField(activeTab, key, value)}
              readOnly={!canManageDriver}
            />
          ) : null}
        </div>
      </section>

      {showEditModal && formDraft && (
        <div className="drivers-modal">
          <div className="drivers-modal-inner">
            <h3>Editar conductor</h3>
            <DriverForm
              value={formDraft}
              onChange={setFormDraft}
              passes={passes}
              shifts={shifts}
              shiftGroups={shiftGroups}
              vehicles={vehicles}
              fieldErrors={fieldErrors}
              showStatusField={false}
            />
            <div className="ed-driver__modal-actions">
              <ButtonCancelForm
                callbackCancel={closeEditModal}
                locked={savingDriver}
                confirmCancel={false}
              />
              <ButtonSaveForm
                callbackSave={callbackSaveDriver}
                locked={savingDriver}
                onComplete={handleSaveDriverComplete}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EditDriverPage;
